/**
 * **设备动作脚本**（`IR → 一串语义化动作`）与它的**模拟器**。
 *
 * ## 它站在 D7 的哪一侧
 *
 * 按 D7：**设备指令（ZPL / TSPL / ESC-POS）只在 C++ 里生成**。
 * 所以这一层刻意停在"**动作**"这一级：
 *
 * ```
 *   IR（几何 + job）  ──►  动作脚本（本文件）  ──►  ZPL / TSPL（C++）
 *                          设介质 / 设浓度 / 打一页 / 切一刀 / 走纸
 * ```
 *
 * 三种好处：
 *   · **可模拟**：不需要打印机就能算出"这份活会打出几张、切几刀、总共走多长纸"；
 *   · **可对拍**：C++ 侧只要保证"动作 → 指令"的映射，几何那部分已经被 S5 钉住了；
 *   · **机型无关**：动作里没有 `^MD` / `DENSITY` 这类名字（那是映射的事）。
 *
 * ## ⚠️ 边界
 *
 * * 这里**不产指令**（一行 ZPL 都不产）；
 * * 动作只覆盖"作业级"的东西（份数 / 切 / 走纸 / 浓度 / 速度 / 介质），
 *   页码与几何在 IR 里，不由动作重复表达。
 */
import { normalizeJob, type PrintJob } from './job'
import type { PrintRenderTreeFile, PageRenderTree } from './renderTree'
import { rasterizePage, type RasterOptions } from './raster'

/** 一条设备动作（语义级；执行侧把它翻成自己机型的指令） */
export type JobStep =
  /** 设介质检测方式（间隙 / 黑标 / 连续） */
  | { action: 'media'; media: PrintJob['media'] }
  /** 设浓度（0–30 相对值） */
  | { action: 'darkness'; darkness: number }
  /** 设速度档 */
  | { action: 'speed'; speed: PrintJob['speed'] }
  /** 打一页（`index` 是这一份里的第几页，0 基） */
  | { action: 'print-page'; index: number; pageId: string }
  /** 切一刀 */
  | { action: 'cut' }
  /** 额外走纸（mm） */
  | { action: 'feed'; mm: number }

/**
 * **一份活 → 动作序列**。
 *
 * 顺序（这是"打出来对不对"的核心，别改）：
 *
 * ```
 *   设介质 / 浓度 / 速度（一次性）
 *   ┌ 每一份：
 *   │   ┌ 每一页：打一页
 *   │   └ 若「每张切」→ 切一刀
 *   └ 若「打完切」→ 切一刀
 *   若走纸 > 0 → 走纸
 * ```
 *
 * ⚠️ "每张切"切的是**每一份的最后一页之后**（一份 = 一张标签）——
 * 多页文档里"每张切"的用户预期就是"每份之间断开"，而不是"每页都切"。
 * 这两者差异在 3 页 × 5 份上非常明显（切 5 刀 vs 切 15 刀）。
 */
export function jobScriptOf(file: PrintRenderTreeFile): JobStep[] {
  const job = normalizeJob(file.job)
  const steps: JobStep[] = [
    { action: 'media', media: job.media },
    { action: 'darkness', darkness: job.darkness },
    { action: 'speed', speed: job.speed },
  ]
  for (let copy = 0; copy < job.copies; copy += 1) {
    file.pages.forEach((page, index) => {
      steps.push({ action: 'print-page', index, pageId: page.pageId })
    })
    if (job.cut === 'after-each') steps.push({ action: 'cut' })
  }
  if (job.cut === 'after-job') steps.push({ action: 'cut' })
  if (job.feedMm > 0) steps.push({ action: 'feed', mm: job.feedMm })
  return steps
}

export interface JobSimulation {
  /** 一共打出几张标签（= 份数 × 页数） */
  labels: number
  /** 切几刀 */
  cuts: number
  /** 总走纸长度（mm）—— 读者最关心的那个数（一卷纸能打几份） */
  lengthMm: number
  /** 每一页的墨覆盖率（0–1）—— 浓度 / 阈值调得对不对，靠它看 */
  inkRatioByPage: number[]
  /** 每张标签的走纸长度（页高之和 + 切缝），用于"一卷能打几张" */
  labelMm: number
  steps: JobStep[]
}

export interface SimulateOptions extends RasterOptions {
  /**
   * 切缝 / 标签间隔（mm）。
   *
   * ⚠️ 它是**估算**用的：真实间隔由纸与机器决定（间隙纸本身就是为它存在的）。
   * 默认 2mm 是常见间隙纸的量级，写在这里是为了让"一卷能打几张"这个数
   * 有个明确来源 —— 而不是让用户以为它精确到了 0.1mm。
   */
  gapMm?: number
}

/**
 * **模拟一遍**：不碰打印机，算出这份活会做什么。
 *
 * 墨覆盖率走的是**参考光栅器**（S4）—— 于是"这份模板大概多费墨"和
 * "打出来是什么样"是同一个数据源，不会各说各话。
 */
export function simulateJob(
  file: PrintRenderTreeFile,
  options: SimulateOptions = {},
): JobSimulation {
  const job = normalizeJob(file.job)
  const steps = jobScriptOf(file)
  const gapMm = options.gapMm ?? 2
  const pageHeightMm = (page: PageRenderTree): number => (page.heightDots * 25.4) / page.dpi

  const perCopyMm = file.pages.reduce((sum, page) => sum + pageHeightMm(page), 0)
  const labels = job.copies * file.pages.length
  const cuts = steps.filter((step) => step.action === 'cut').length
  /* 每一份之间要留间隙；最后一份之后也要走完最后一张 */
  const lengthMm =
    labels === 0
      ? 0
      : perCopyMm * job.copies + gapMm * Math.max(0, job.copies - 1) + job.feedMm

  const inkRatioByPage = file.pages.map((page) => {
    const raster = rasterizePage(page, options)
    const total = raster.widthDots * raster.heightDots
    return total > 0 ? raster.stats.ink / total : 0
  })

  return {
    labels,
    cuts,
    lengthMm: Math.round(lengthMm * 100) / 100,
    inkRatioByPage,
    labelMm: Math.round((perCopyMm + gapMm) * 100) / 100,
    steps,
  }
}

/** 给人看的一句话（面板上显示"这份活会做什么"） */
export function describeSimulation(simulation: JobSimulation): string {
  const ink = simulation.inkRatioByPage.map((ratio) => `${Math.round(ratio * 100)}%`).join(' / ')
  return `${simulation.labels} 张 · 切 ${simulation.cuts} 刀 · 走纸约 ${Math.round(simulation.lengthMm)}mm · 上墨 ${ink}`
}
